import React, { useEffect, useRef, useState } from 'react';

function FilterInfoIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <circle cx="10" cy="10" r="7.25" stroke="currentColor" strokeWidth="1.6" />
      <path d="M10 9.1V14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <circle cx="10" cy="6.4" r="0.95" fill="currentColor" />
    </svg>
  );
}

/**
 * Desktop: inline filter chips.
 * Responsive: one info icon + count; hover/tap shows applied filters and status legend.
 */
export default function ToolbarFilterInfo({
  items = [],
  statuses = [],
  ariaLabel = 'Active filters',
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const filterItems = items.filter((item) => item && !item.isCount && item.value);
  const extraItems = items.filter((item) => item && item.isCount && item.value);
  const tooltipItems = [...filterItems, ...extraItems];
  const count = filterItems.length;

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  if (!tooltipItems.length && !statuses.length) return null;

  const renderChip = (item) => (
    <span
      key={item.key}
      className={`toolbar-filter-info__item${item.isCount ? ' toolbar-filter-info__item--count' : ''}`}
    >
      {item.icon ? <i className={item.icon} aria-hidden /> : null}
      {item.label ? <span className="toolbar-filter-info__label">{item.label}</span> : null}
      <span className="toolbar-filter-info__value">{item.value}</span>
    </span>
  );

  return (
    <div
      className={`toolbar-filter-info${open ? ' is-open' : ''}`}
      aria-label={ariaLabel}
      ref={wrapRef}
    >
      {items.length > 0 ? (
        <div className="toolbar-filter-info__chips">
          {items.map(renderChip)}
        </div>
      ) : null}
      <div className="toolbar-filter-info__compact">
        <button
          type="button"
          className="toolbar-filter-info__summary"
          aria-label={
            count
              ? `${count} filter${count === 1 ? '' : 's'} applied`
              : 'Status legend and filters'
          }
          aria-expanded={open}
          onClick={() => setOpen((current) => !current)}
        >
          <FilterInfoIcon />
          {count > 0 ? <span className="toolbar-filter-info__badge">{count}</span> : null}
        </button>
        <div className="toolbar-filter-info__tooltip" role="tooltip">
          {statuses.length > 0 ? (
            <>
              <div className="toolbar-filter-info__tooltip-title">Status</div>
              <div className="toolbar-filter-info__status-list">
                {statuses.map((status) => (
                  <div key={status.key} className="toolbar-filter-info__status-item">
                    <span className={`legend-dot ${status.dotClass}`} />
                    <span>{status.label}</span>
                  </div>
                ))}
              </div>
            </>
          ) : null}
          {tooltipItems.length > 0 ? (
            <>
              <div className="toolbar-filter-info__tooltip-title">Applied filters</div>
              {tooltipItems.map((item) => (
                <div key={`tip-${item.key}`} className="toolbar-filter-info__tooltip-row">
                  {item.label ? (
                    <span className="toolbar-filter-info__tooltip-label">{item.label}</span>
                  ) : null}
                  <span className="toolbar-filter-info__tooltip-value">{item.value}</span>
                </div>
              ))}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
